/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */

import { AssistElement, EvaluationLine } from '../../types/model';
import React, { FC, useContext, useEffect } from 'react';
import { EvaluationLineEditor } from './EvaluationLineEditor';
import { useEvaluationAlign } from './hooks/useEvaluationAlign';
import { PopoverContext } from './context/PopoverContextProvider';
import { underlineStyle } from './utils/AssistUtils';

interface EvaluationLineVisualizationProps {
    line: EvaluationLine;
    assist: AssistElement[];
    currentPartIndex?: number;
    nodeIdsNearCursor?: number[];
}

export const EvaluationLineDecoration: FC<EvaluationLineVisualizationProps> = ({ line, assist, currentPartIndex, nodeIdsNearCursor }) => {
    const align = useEvaluationAlign(line.id);
    const { placePopover, removePopovers } = useContext(PopoverContext);

    useEffect(() => {
        const editor = line.editor;
        if (!editor) {
            return;
        }
        removePopovers(line.id);
        editor.cleanDecorations();
        if (assist) {
            assist.forEach((a) => {
                editor.decorateNodes(
                    a.accurateNodeIds,
                    (element) => {
                        underlineStyle(a.status, element);
                        const active = a.partIndex === currentPartIndex && editor.getSymbolsAtCursor(true).some((id) => a.accurateNodeIds.includes(id));
                        placePopover({ element, assist: a, lineId: a.lineId, partIndex: a.partIndex, active });
                    },
                    () => {},
                    true,
                    true
                );
            });
        }
        align();
    }, [assist, line.editor, line.id, currentPartIndex, line.tree, nodeIdsNearCursor, align, placePopover, removePopovers]);

    return <EvaluationLineEditor key={line.id} line={line} assist={assist} />;
};
